import { inject, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MBPError, toMbpError } from './mbp-error';
import { ErrorMessageService } from './error-message.service';
import { ClientErrorLogService } from './client-error-log.service';

@Service()
export class ErrorHandlingService {
  private static readonly VALIDATION_ERROR_STATUS = 460;
  private static readonly ERROR_CODE_TRANSLATION_KEYS = new Map<number, string>([
    [1, 'errors.validation.mandatoryFieldMissing'],
    [2, 'errors.validation.maximumCharactersReached'],
    [3, 'errors.validation.duplicateEntry'],
    [4, 'errors.validation.insufficientPermissions'],
    [5, 'errors.validation.externalServiceError'],
    [6, 'errors.validation.invalidValue']
  ]);

  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly translate = inject(TranslateService);
  private readonly errorLogger = inject(ClientErrorLogService);

  async handleErrorAsync(error: unknown, message?: string): Promise<never> {
    const mbpError = toMbpError(error);

    if (this.isValidationError(mbpError)) {
      await this.displayValidationErrorMessage(mbpError);
      mbpError.logError = false;
      return Promise.reject(mbpError);
    }

    if (message) {
      await this.errorMessageService.displayErrorMessage(this.translate.instant('general.error'), message);
    }
    mbpError.handled = true;
    mbpError.logError = true;
    return Promise.reject(mbpError);
  }

  isValidationError(error: MBPError): boolean {
    return error.statusCode === ErrorHandlingService.VALIDATION_ERROR_STATUS;
  }

  silentLogError(error: unknown): void {
    void this.errorLogger.logErrorAsync(error);
  }

  private async displayValidationErrorMessage(error: MBPError): Promise<void> {
    const errorMessage: string = this.getTranslatedValidationErrorMessage(error.validationErrorCode);
    await this.errorMessageService.displayValidationErrorMessage(errorMessage);
    error.handled = true;
  }

  private getTranslatedValidationErrorMessage(code?: number): string {
    if (!code) {
      return this.translate.instant('errors.validation.default');
    }

    const translationKey = ErrorHandlingService.ERROR_CODE_TRANSLATION_KEYS.get(code);
    return translationKey ? this.translate.instant(translationKey) : this.translate.instant('errors.validation.default');
  }
}
